import React, { useState, useEffect } from 'react';
import { FiX, FiArrowDown, FiArrowUp, FiEdit3 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../services/api';
import Spinner from './Spinner';

const getMovementDetails = (item) => {
    switch (item.type) {
        case 'vente':
            return { icon: <FiArrowDown className="text-red-500" />, label: 'Vente' };
        case 'achat':
            return { icon: <FiArrowUp className="text-green-500" />, label: 'Achat' };
        case 'ajustement manuel':
        case 'ajustement':
            return { icon: <FiEdit3 className="text-yellow-500" />, label: item.reason || item.description || 'Ajustement' };
        default:
            return { icon: null, label: item.type };
    }
};

const ProductHistoryModal = ({ isOpen, onClose, productId, productName, currentProduct }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isOpen && productId) {
            const fetchHistory = async () => {
                setLoading(true);
                try {
                    const historyResponse = await api.get(`/products/${productId}/history`);
                    let historyData = historyResponse.data;
                    const productData = currentProduct;

                    // Si le produit a des variantes, on recalcule le "Stock final" de chaque mouvement
                    // car la valeur stockée peut être basée sur le total du produit parent (qui n'est pas mis à jour)
                    // au lieu de la somme des variantes.
                    if (productData && productData.variants && productData.variants.length > 0 && historyData.length > 0) {
                        // 1. On calcule le stock actuel réel à partir des variantes
                        const currentTotalStock = productData.variants.reduce((sum, v) => sum + (parseInt(v.quantity, 10) || 0), 0);

                        // 2. On corrige l'historique en partant de la fin (le plus récent)
                        let lastKnownStock = currentTotalStock;
                        const correctedHistory = historyData.map(movement => {
                            const correctedMovement = { ...movement, newQuantity: lastKnownStock };
                            // Le stock final du mouvement précédent était le stock actuel avant ce mouvement.
                            lastKnownStock -= movement.quantityChange;
                            return correctedMovement;
                        });
                        setHistory(correctedHistory);
                    } else {
                        setHistory(historyData);
                    }
                } catch (error) {
                    const errorMessage = error.response?.data?.message || "Impossible de charger l'historique du produit.";
                    toast.error(errorMessage);
                } finally {
                    setLoading(false);
                }
            };
            fetchHistory();
        }
    }, [isOpen, productId, currentProduct]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[80vh]">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold">Historique pour "{productName}"</h3>
                    <button onClick={onClose}><FiX size={24} /></button>
                </div>
                <div className="overflow-y-auto">
                    {loading ? <Spinner message="Chargement de l'historique..." /> : (
                        <ul className="space-y-3">
                            {history.length > 0 ? history.map(item => {
                                const { icon, label } = getMovementDetails(item);
                                return (
                                    <li key={item._id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center">
                                            <span className="mr-3">{icon}</span>
                                            <div>
                                                <p className="font-semibold">{label}</p>
                                                <p className="text-xs text-gray-500">{new Date(item.date).toLocaleString('fr-FR')}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className={`font-bold ${item.quantityChange > 0 ? 'text-green-600' : 'text-red-600'}`}>{item.quantityChange > 0 ? `+${item.quantityChange}` : item.quantityChange}</p>
                                            <p className="text-xs text-gray-500">Stock final: {item.newQuantity}</p>
                                        </div>
                                    </li>
                                );
                            }) : <p className="text-center text-gray-500 py-8">Aucun historique de mouvement pour ce produit.</p>}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductHistoryModal;